import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { projectRipsOntoTargetWalls } from '@structuralPlanningUtils'
import { AxiosError } from 'axios'
import DeleteIcon from '@mui/icons-material/Delete'
import { Button, IconButton, Stack, Typography } from '@mui/material'
import { ThreeEvent } from '@react-three/fiber'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore, useElementSelectionStore, useModelStore } from '@editorStores'
import {
  useElementLabel,
  useModelClickListeners,
  useResultsInvalidation,
  useSelectionMode,
  useTypeInteraction,
} from '@editorHooks'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createRips } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'

export const COPY_RIP_TARGET_SELECTION_KEY = 'copy-rip-target-walls'
export const COPY_RIPS_MULTI_TARGET_SELECTION_KEY = 'copy-rips-multi-target-walls'

const wallTypeInteraction: ElementTypes[] = ['inner_walls', 'outer_walls']

interface Props {
  rips: Rip[]
  selectionModeKey?: string
}

const RipCopyForm = ({
  rips,
  selectionModeKey = COPY_RIP_TARGET_SELECTION_KEY,
}: Props): ReactElement | null => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const client = useQueryClient()
  const invalidateResults = useResultsInvalidation()
  const getLabel = useElementLabel()
  const walls = useModelStore(state => state.model.walls)
  const {
    setSelectionMode,
    unsetSelectionMode,
    selectionModeKey: activeSelectionModeKey,
  } = useSelectionMode()
  const setHighlightedIds = useElementSelectionStore(state => state.setHighlightedIds)
  const resetHighlightedIds = useElementSelectionStore(state => state.resetHighlightedIds)
  const problemViewActiveErrors = useControlStore(state => state.problemViewActiveErrors)
  const problemViewActiveWarnings = useControlStore(state => state.problemViewActiveWarnings)
  const setProblemViewActiveErrors = useControlStore(state => state.setProblemViewActiveErrors)
  const setProblemViewActiveWarnings = useControlStore(state => state.setProblemViewActiveWarnings)

  const previousProblemViewRef = useRef({ errors: true, warnings: true })
  const [targetWallGuids, setTargetWallGuids] = useState<Set<string>>(new Set())

  const excludeWallGuids = useMemo(() => new Set(rips.map(rip => rip.wall_guid)), [rips])
  const isSelectingTargetWalls = activeSelectionModeKey === selectionModeKey
  const isMultiRipCopy = rips.length > 1

  useTypeInteraction(wallTypeInteraction, isSelectingTargetWalls)

  const toggleTargetWall = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      const { elementType } = event.object.userData as { elementType?: string }
      if (elementType !== 'inner_walls' && elementType !== 'outer_walls') return

      const wallGuid = event.object.name
      if (excludeWallGuids.has(wallGuid)) return

      setTargetWallGuids(prev => {
        const next = new Set(prev)
        if (next.has(wallGuid)) next.delete(wallGuid)
        else next.add(wallGuid)
        return next
      })
    },
    [excludeWallGuids],
  )

  useModelClickListeners(
    toggleTargetWall,
    [excludeWallGuids, toggleTargetWall],
    isSelectingTargetWalls,
    true,
  )

  useEffect(() => {
    setHighlightedIds(Array.from(targetWallGuids))
    return () => resetHighlightedIds()
  }, [targetWallGuids, setHighlightedIds, resetHighlightedIds])

  const onStartSelectingTargetWalls = useCallback(() => {
    previousProblemViewRef.current = {
      errors: problemViewActiveErrors,
      warnings: problemViewActiveWarnings,
    }
    setProblemViewActiveErrors(false)
    setProblemViewActiveWarnings(false)
    setSelectionMode({
      key: selectionModeKey,
      message: isMultiRipCopy
        ? 'Klicken Sie im Modell auf Wände, auf die die Rippen kopiert werden sollen. Schließen Sie diese Meldung zum Beenden.'
        : 'Klicken Sie im Modell auf Wände, auf die die Rippe kopiert werden soll. Schließen Sie diese Meldung zum Beenden.',
      onAbort: () => {
        setProblemViewActiveErrors(previousProblemViewRef.current.errors)
        setProblemViewActiveWarnings(previousProblemViewRef.current.warnings)
      },
    })
  }, [
    isMultiRipCopy,
    problemViewActiveErrors,
    problemViewActiveWarnings,
    selectionModeKey,
    setProblemViewActiveErrors,
    setProblemViewActiveWarnings,
    setSelectionMode,
  ])

  const { mutateAsync: copyRipsToWalls, isLoading: isCopying } = useMutation(
    async (targetGuids: string[]) => {
      const targetWalls = walls.filter(w => targetGuids.includes(w.guid))
      const ripsToCreate = projectRipsOntoTargetWalls(rips, targetWalls)

      if (ripsToCreate.length === 0) {
        enqueueSnackbar(
          isMultiRipCopy
            ? 'Rippen konnten auf keiner der ausgewählten Wände platziert werden'
            : 'Rippe konnte auf keiner der ausgewählten Wände platziert werden',
          { variant: 'warning' },
        )
        return
      }

      await createRips.request(projectId, ripsToCreate)
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        setProblemViewActiveErrors(previousProblemViewRef.current.errors)
        setProblemViewActiveWarnings(previousProblemViewRef.current.warnings)
        setTargetWallGuids(new Set())
        unsetSelectionMode()
        enqueueSnackbar(
          isMultiRipCopy
            ? 'Rippen erfolgreich auf andere Wände kopiert'
            : 'Rippe erfolgreich auf andere Wände kopiert',
          { variant: 'success' },
        )
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(
          buildErrorMessage(
            error,
            isMultiRipCopy ? 'Fehler beim Kopieren der Rippen' : 'Fehler beim Kopieren der Rippe',
          ),
          { variant: 'error' },
        )
      },
    },
  )

  if (rips.length === 0) return null

  return (
    <Stack direction="column" spacing={1} width="100%">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography align="center" noWrap sx={{ width: '100%' }}>
          {targetWallGuids.size} Zielwände ausgewählt
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onStartSelectingTargetWalls}
          fullWidth
          disabled={isCopying}
          data-cy="btn-select-copy-rip-target-walls"
        >
          Wände auswählen
        </Button>
      </Stack>
      {targetWallGuids.size > 0 && (
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 1,
            width: '100%',
          }}
        >
          {Array.from(targetWallGuids).map(guid => (
            <Stack key={guid} direction="row" alignItems="center" spacing={1}>
              <Typography noWrap align="center">
                {getLabel(guid)}
              </Typography>
              <IconButton
                size="small"
                aria-label="Zielwand entfernen"
                onClick={() =>
                  setTargetWallGuids(prev => {
                    const next = new Set(prev)
                    next.delete(guid)
                    return next
                  })
                }
                sx={{ padding: 0 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <SaveButton
        size="small"
        onClick={() => copyRipsToWalls(Array.from(targetWallGuids))}
        loading={isCopying}
        disabled={targetWallGuids.size === 0}
        fullWidth
        data-cy={isMultiRipCopy ? 'btn-copy-rips' : 'btn-copy-rip'}
      >
        Kopieren
      </SaveButton>
    </Stack>
  )
}

export default RipCopyForm
